import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  UserPlus, ArrowLeft, Copy, Check, AlertCircle, ExternalLink, Upload, Download, Loader2,
  CheckCircle2, ChevronRight,
} from 'lucide-react';
import { tr } from '../lib/i18nHelpers';
import CoaPreview, { type CoaMode, type CoaAccount } from '../components/CoaPreview';
import CoaMapReview, { reviewDraftStorageKey, type CoaImportProposal } from '../components/CoaMapReview';
import { buildStrictCoaImportProposals, readCoaImportSource, readStrictCoaImportRows } from '../lib/coaImport';
import { COA_IMPORT_AI_ANALYSIS_DISABLED } from '../lib/systemFlags';

const STEP_COMPANY = 1;
const STEP_ACCOUNTING = 2;
const STEP_REVIEW = 3;
const STEP_COMPLETE = 4;

const STEP_META = [
  { id: STEP_COMPANY, label: tr('Company & access', '公司及存取', '公司及存取') },
  { id: STEP_ACCOUNTING, label: tr('Accounting setup', '會計設定', '会计设定') },
  { id: STEP_REVIEW, label: tr('Review & create', '確認及建立', '确认及建立') },
  { id: STEP_COMPLETE, label: tr('Created', '已建立', '已建立') },
];

export default function NewClient() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { switchClient, refreshClients } = useAuth();
  const [form, setForm] = useState({
    company_name: '',
    contact_email: '',
    contact_name: '',
    initial_password: '',
    industry: '',
    fy_start: '04',
    fy_end: '03',
  });
  const [result, setResult] = useState<{ user_id: string; email: string; password: string; clientId: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [coaMode, setCoaMode] = useState<CoaMode>('manual');
  const [customAccounts, setCustomAccounts] = useState<CoaAccount[]>([]);
  const [removedCodes, setRemovedCodes] = useState<Set<string>>(new Set());
  const [openingBalances, setOpeningBalances] = useState<Record<string, number>>({});
  const [finalAccounts, setFinalAccounts] = useState<CoaAccount[]>([]);
  const [coaChoice, setCoaChoice] = useState<'predefined' | 'import'>('predefined');
  const [mapProposals, setMapProposals] = useState<CoaImportProposal[] | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapNotice, setMapNotice] = useState<string | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapProvider, setMapProvider] = useState<string | null>(null);
  const [mapUsage, setMapUsage] = useState<{ prompt: number; completion: number; total: number } | null>(null);
  const [mapApplied, setMapApplied] = useState(false);
  const coaFileRef = useRef<HTMLInputElement>(null);
  const [showLogin, setShowLogin] = useState(false);

  const requestedReturn = searchParams.get('return') || '/';
  const returnTo = requestedReturn.startsWith('/') && !requestedReturn.startsWith('//') ? requestedReturn : '/';
  const parsedStep = Number(searchParams.get('step') || STEP_COMPANY);
  const activeStep = result
    ? STEP_COMPLETE
    : [STEP_COMPANY, STEP_ACCOUNTING, STEP_REVIEW].includes(parsedStep)
      ? parsedStep
      : STEP_COMPANY;

  const goToStep = (step: number) => {
    if (result) return;
    const next = new URLSearchParams(searchParams);
    if (step === STEP_COMPANY) next.delete('step');
    else next.set('step', String(step));
    setSearchParams(next, { replace: false });
  };

  // Default COA mode: Industry when an industry classification is selected, Manual otherwise
  useEffect(() => {
    if (form.industry) {
      setCoaMode('industry');
    } else {
      setCoaMode('manual');
    }
  }, [form.industry]);

  // ── Upload persistence (survives a refresh) ──────────────────────────────
  const COA_DRAFT_PAGE_KEY = 'coaImportDraft:v1:new-client';
  const COA_DRAFT_REVIEW_KEY = 'new-client';
  const clearCoaDraft = () => {
    try {
      localStorage.removeItem(COA_DRAFT_PAGE_KEY);
      localStorage.removeItem(reviewDraftStorageKey(COA_DRAFT_REVIEW_KEY));
    } catch { /* ignore */ }
  };

  // Restore a previously uploaded/mapped review on mount.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COA_DRAFT_PAGE_KEY) || 'null');
      if (saved && Array.isArray(saved.proposals) && saved.proposals.length) {
        setCoaChoice('import');
        setMapProposals(saved.proposals);
        setMapProvider(saved.provider ?? null);
        setMapUsage(saved.usage ?? null);
        setFinalAccounts(saved.finalAccounts ?? []);
        setOpeningBalances(saved.openingBalances ?? {});
        setMapApplied(!!saved.mapApplied);
      }
    } catch { /* ignore malformed draft */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist whenever the review state changes.
  useEffect(() => {
    if (!mapProposals?.length) return;
    try {
      localStorage.setItem(COA_DRAFT_PAGE_KEY, JSON.stringify({
        proposals: mapProposals, provider: mapProvider, usage: mapUsage,
        finalAccounts, openingBalances, mapApplied,
      }));
    } catch { /* ignore quota errors */ }
  }, [mapProposals, mapProvider, mapUsage, finalAccounts, openingBalances, mapApplied]);

  // Industry template used to seed the downloadable Excel template on the import branch.
  const { data: importTemplateData } = useQuery({
    queryKey: ['coa-import-template', form.industry || 'professional'],
    queryFn: () => api(
      `/bookkeeping/accounts/template?industry=${encodeURIComponent(form.industry || 'professional')}&mode=industry`,
      { skipActiveClient: true },
    ),
    enabled: coaChoice === 'import',
  });
  const importTemplateAccounts: CoaAccount[] = ((importTemplateData as any)?.data || []);

  const chooseCoa = (choice: 'predefined' | 'import') => {
    if (choice === coaChoice) return;
    clearCoaDraft();
    setCoaChoice(choice);
    // Both branches converge on finalAccounts/openingBalances — clear stale data so a
    // branch switch can never leak the other branch's accounts into the payload.
    setFinalAccounts([]);
    setOpeningBalances({});
    setRemovedCodes(new Set());
    setCustomAccounts([]);
    setMapProposals(null);
    setMapProvider(null);
    setMapUsage(null);
    setMapApplied(false);
    setMapError(null);
    setMapNotice(null);
    setMapLoading(false);
  };

  const downloadImportTemplate = async () => {
    const { coaTemplateXlsx } = await import('../lib/coaImport');
    const blob = await coaTemplateXlsx(importTemplateAccounts);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'coa-template.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  // Import branch: parse the file, ask the mapper to propose target accounts,
  // then hand the proposals to CoaMapReview for a human decision. Nothing is
  // written here — Apply lands in finalAccounts/openingBalances, which feed the
  // existing accounts[] submit payload.
  const handleCoaFile = async (file: File) => {
    // A new file supersedes any persisted review for the previous file.
    try { localStorage.removeItem(reviewDraftStorageKey(COA_DRAFT_REVIEW_KEY)); } catch { /* ignore */ }
    setMapError(null);
    setMapNotice(null);
    setMapProposals(null);
    setMapProvider(null);
    setMapUsage(null);
    setMapApplied(false);
    setMapLoading(true);
    // A new file invalidates any previously applied import: drop the stale
    // accounts/balances so the summary can't show file A while file B is reviewed.
    setFinalAccounts([]);
    setOpeningBalances({});
    try {
      // Live-demo safety flag: no LLM call and no inferred replacements. The
      // strict reader accepts only template-shaped rows; review defaults carry
      // the uploaded code/name/balance through unchanged.
      if (COA_IMPORT_AI_ANALYSIS_DISABLED) {
        const rows = await readStrictCoaImportRows(file);
        const proposals = buildStrictCoaImportProposals(rows);
        if (proposals.length === 0) {
          setMapError(tr('No accounts found in the file.', '檔案中找不到科目。', '檔案中找不到科目。'));
          return;
        }
        setMapProposals(proposals);
        return;
      }

      const industry = form.industry || 'professional';
      // Hybrid input (D8): parsed rows when the strict reader accepts them,
      // otherwise the first sheet as raw CSV text for the LLM to parse.
      const source = await readCoaImportSource(file);
      if (!source.rows && !source.rawText) {
        setMapError(tr('No accounts found in the file.', '檔案中找不到科目。', '檔案中找不到科目。'));
        return;
      }
      const res = await api('/firms/coa-map', {
        method: 'POST',
        // Import has no tenant context involved, so a stale X-Active-Client
        // selection must not break the import flow.
        skipActiveClient: true,
        // LLM import can take a while for large files — allow 120s.
        timeoutMs: 120000,
        body: { industry, ...source },
      }) as { proposals?: CoaImportProposal[]; total?: number; truncated?: boolean; provider?: string; usage?: { prompt: number; completion: number; total: number }; llmRaw?: string | null };
      setMapProvider(res?.provider ?? null);
      setMapUsage(res?.usage ?? null);
      if (res?.truncated) {
        setMapNotice(tr(
          `The file has ${res.total ?? 'more than 500'} accounts — only the first 500 were mapped.`,
          `檔案有 ${res.total ?? '超過 500'} 個科目——僅對應了首 500 個。`,
          `档案有 ${res.total ?? '超过 500'} 个科目——仅对应了首 500 个。`,
        ));
      }
      const proposals = res?.proposals ?? [];
      if (proposals.length === 0) {
        setMapError(tr('No account mappings could be generated.', '無法產生科目對應。', '无法产生科目对应。'));
        return;
      }
      setMapProposals(proposals);
    } catch (e: any) {
      setMapError(e?.message || e?.error || String(e));
    } finally {
      setMapLoading(false);
    }
  };

  const applyMappedCoa = (accounts: CoaAccount[], balances: Record<string, number>) => {
    if (accounts.length === 0) {
      // All rows excluded — keep the review open so the user can re-include
      // rather than silently submitting an empty COA.
      setMapError(tr('No accounts selected — include at least one row to apply.', '未選擇科目——請至少包括一行。', '未选择科目——请至少包括一行。'));
      return;
    }
    setFinalAccounts(accounts);
    setOpeningBalances(balances);
    setMapApplied(true);
    setMapError(null);
    setMapLoading(false);
  };

  // Duplicate-name conflict (2026-10-30): surface the 409 inline instead of a
  // bare alert — the user can switch to the existing client or create anyway.
  const [dupConflict, setDupConflict] = useState<{ id: string; client_user_id: string; display_name: string | null; created_at: string } | null>(null);

  const switchToExistingClient = () => {
    if (!dupConflict) return;
    // Write the stored client directly, then hard-navigate — a reload guarantees
    // AuthContext and every query re-init with the new X-Active-Client.
    localStorage.setItem('activeClient', JSON.stringify({
      id: dupConflict.id,
      client_user_id: dupConflict.client_user_id,
      display_name: dupConflict.display_name || form.company_name,
    }));
    refreshClients();
    window.location.assign('/dashboard');
  };

  const createMut = useMutation({
    mutationFn: (opts?: { allowDuplicate?: boolean }) => api('/firms/my/clients', {
      method: 'POST',
      body: {
        company_name: form.company_name,
        email: showLogin ? form.contact_email : undefined,
        contact_name: form.contact_name || undefined,
        initial_password: showLogin ? (form.initial_password || undefined) : undefined,
        industry: form.industry || undefined,
        fy_start: form.fy_start || undefined,
        fy_end: form.fy_end || undefined,
        coa_choice: coaChoice,
        coa_mode: coaMode,
        coa_industry: form.industry || 'professional',
        ...(opts?.allowDuplicate ? { allow_duplicate_name: true } : {}),
        accounts: finalAccounts.map(a => ({
          account_code: a.account_code,
          account_name: a.account_name,
          account_type: a.account_type,
          parent_code: a.parent_code,
          ...(openingBalances[a.account_code] ? { opening_balance: openingBalances[a.account_code] } : {}),
        })),
      },
    }) as Promise<{ user_id: string; client_user_id?: string; id: string; password?: string; success: boolean }>,
    onSuccess: (res: any) => {
      setDupConflict(null);
      setResult({
        user_id: res.user_id || res.client_user_id,
        email: res.email || form.contact_email,
        password: res.password || form.initial_password,
        clientId: res.id,
      });
      refreshClients(); // Refresh dropdown immediately
      clearCoaDraft();  // client created — the persisted review is no longer needed
    },
    onError: (err: any) => {
      if (err?.existing_client) {
        setDupConflict(err.existing_client);
        return;
      }
      alert(tr(
        `Could not create client: ${err?.error || err?.message || 'unknown error'}`,
        `無法建立客戶：${err?.error || err?.message || '未知錯誤'}`,
        `無法建立客戶：${err?.error || err?.message || '未知错误'}`,
      ));
    },
  });

  const generatePassword = () => {
    const words = ['sunny', 'happy', 'lucky', 'smart', 'quick', 'bright', 'clear', 'gentle'];
    const w = words[Math.floor(Math.random() * words.length)];
    const digits = Math.floor(1000 + Math.random() * 9000);
    setForm(f => ({ ...f, initial_password: `${w}-${digits}` }));
  };

  const copy = (label: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const loginReady = !showLogin || (form.contact_email.trim() && form.initial_password.length >= 6);
  // The import branch may only be submitted once a mapping has been applied
  // (finalAccounts populated) and nothing is in-flight/failed. Predefined is
  // unaffected.
  const importSubmissionReady =
    coaChoice !== 'import' || (finalAccounts.length > 0 && !mapLoading && mapApplied && !mapError);
  const companyReady = !!form.company_name.trim() && loginReady;
  const accountingReady = finalAccounts.length > 0 && importSubmissionReady;
  const canSubmit = companyReady && accountingReady && !createMut.isPending;
  const backLabel = returnTo === '/firm/manage/clients'
    ? tr('Back to Clients', '返回客戶', '返回客户')
    : tr('Back to Dashboard', '返回儀表板', '返回仪表板');

  const resetAndAddAnother = () => {
    setResult(null);
    setForm({
      company_name: '', contact_email: '', contact_name: '', initial_password: '',
      industry: '', fy_start: '04', fy_end: '03',
    });
    setOpeningBalances({});
    setFinalAccounts([]);
    setCoaChoice('predefined');
    setRemovedCodes(new Set());
    setCustomAccounts([]);
    setMapProposals(null);
    setMapProvider(null);
    setMapUsage(null);
    setMapApplied(false);
    setMapError(null);
    setMapLoading(false);
    setShowLogin(false);
    const next = new URLSearchParams(searchParams);
    next.delete('step');
    setSearchParams(next);
  };

  if (result) {
    const isPlaceholderLogin = !result.email || result.email.endsWith('@no-login.local');
    return (
      <div className="p-6 w-[95%] mx-auto space-y-5">
        <button type="button" onClick={() => nav(returnTo)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {backLabel}
        </button>
        <CheckoutTimeline activeStep={STEP_COMPLETE} onStepClick={() => {}} />
        <div className="rounded-lg border bg-card p-6">
          <h1 className="text-lg font-bold text-foreground mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-900 dark:text-emerald-100" />
            {tr('Client created successfully', '客戶建立成功', '客户建立成功')}
          </h1>
          <p className="text-sm text-muted-foreground mb-4">
            {isPlaceholderLogin
              ? tr('No login account was created. You can set one later from Firm Management.', '未建立登入帳戶。您可稍後在會計師樓管理中設定。', '未建立登录账户。您可稍后在会计师楼管理中设置。')
              : tr("Share these credentials with your client. The chart of accounts (HK) has been seeded.", "將這些憑證分享給您的客戶。會計科目表(HK)已建立。", "将这些凭证分享给您的客户。会计科目表(HK)已建立。")}
          </p>
          <div className="rounded border bg-muted/30 p-3 space-y-2 font-mono text-sm">
            <CredentialRow label={tr('Company', '公司', '公司')} value={form.company_name} copied={copied === 'Company'} onCopy={() => copy('Company', form.company_name)} />
            {!isPlaceholderLogin && (
              <>
                <CredentialRow label={tr('Login Email', '登入電郵', '登录电邮')} value={result.email} copied={copied === 'Login Email'} onCopy={() => copy('Login Email', result.email)} />
                <CredentialRow label={tr('Password', '密碼', '密码')} value={result.password} copied={copied === 'Password'} onCopy={() => copy('Password', result.password)} />
              </>
            )}
            <CredentialRow label={tr('Industry', '行業', '行业')} value={form.industry || 'general'} copied={false} onCopy={() => {}} />
            <CredentialRow label={tr('Financial Year', '會計年度', '会计年度')} value={`${new Date(2024, parseInt(form.fy_start)-1).toLocaleString('default', { month: 'long' })} → ${new Date(2024, parseInt(form.fy_end)-1).toLocaleString('default', { month: 'long' })}`} copied={false} onCopy={() => {}} />
          </div>
          {!isPlaceholderLogin && (
            <div className="mt-4 rounded border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{tr('Store this password safely. Advise the client to change it after first login.', '請安全保存此密碼。建議客戶在首次登入後更改。', '请安全保存此密码。建议客户在首次登录后更改。')}</span>
            </div>
          )}
          <div className="mt-4 flex gap-2 flex-wrap">
            <button onClick={() => { switchClient(result.clientId); nav('/'); }}
              className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium flex items-center gap-1">
              <ExternalLink className="h-4 w-4" />
              {tr('View Client Dashboard', '查看客戶儀表板', '查看客户仪表板')}
            </button>
            <button onClick={resetAndAddAnother} className="px-4 py-2 border rounded text-sm">
              {tr('Add another client', '新增其他客戶', '新增其他客户')}
            </button>
            <button onClick={() => nav(returnTo)} className="px-4 py-2 border rounded text-sm">
              {backLabel.replace(/^Back to /, 'Return to ')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-[95%] mx-auto space-y-5">
      <button type="button" onClick={() => nav(returnTo)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> {backLabel}
      </button>
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <UserPlus className="h-6 w-6" /> {tr('New Client Company', '新增客戶公司', '新增客户公司')}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {tr("Create a new client tenant with industry-specific COA and financial year setup.", "建立新客戶租戶，配置行業特定會計科目表及會計年度。", "建立新客户租户，配置行业特定会计科目表及会计年度。")}
        </p>
      </div>

      <CheckoutTimeline activeStep={activeStep} onStepClick={goToStep} />

      {activeStep === STEP_COMPANY && (
        <div className="rounded-lg border bg-card p-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">{tr('Company name', '公司名稱', '公司名称')} *</label>
            <input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
              placeholder={tr('e.g. Acme Trading Limited', '例如：Acme Trading Limited', '例如：Acme Trading Limited')}
              className="mt-1 block w-full px-3 py-2 border rounded" />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">{tr('Contact name', '聯絡人', '联络人')}</label>
            <input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
              placeholder={tr('e.g. John Chan', '例如：陳先生', '例如：陈先生')}
              className="mt-1 block w-full px-3 py-2 border rounded" />
          </div>

          {!showLogin ? (
            <button type="button" data-testid="toggle-login" onClick={() => setShowLogin(true)}
              className="text-sm text-primary hover:underline">
              {tr('Create login account for this client', '為此客戶建立登入帳戶', '为此客户建立登录账户')}
            </button>
          ) : (
            <div data-testid="login-section" className="space-y-3 border rounded-lg p-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground">{tr('Login account', '登入帳戶', '登录账户')}</label>
                <button type="button" onClick={() => setShowLogin(false)}
                  className="text-xs text-muted-foreground hover:text-destructive">
                  {tr('Remove login', '移除登入', '移除登录')}
                </button>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">{tr('Login email', '登入電郵', '登录电邮')} *</label>
                <input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                  placeholder="acme@example.com" className="mt-1 block w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">{tr('Initial password', '初始密碼', '初始密码')} * ({tr('at least 6 characters', '至少6個字符', '至少6个字符')})</label>
                <div className="flex gap-2 mt-1">
                  <input type="text" value={form.initial_password} onChange={e => setForm(f => ({ ...f, initial_password: e.target.value }))}
                    placeholder={tr('Type or generate', '輸入或生成', '输入或生成')}
                    className="flex-1 px-3 py-2 border rounded" />
                  <button type="button" onClick={generatePassword} className="px-3 py-2 border rounded text-sm">
                    {tr('Generate', '生成', '生成')}
                  </button>
                </div>
              </div>
            </div>
          )}

          <WizardFooter
            onCancel={() => nav(returnTo)}
            onNext={() => goToStep(STEP_ACCOUNTING)}
            nextDisabled={!companyReady}
            nextLabel={tr('Continue to accounting setup', '繼續會計設定', '继续会计设定')}
          />
        </div>
      )}

      {activeStep === STEP_ACCOUNTING && (
        <div className="rounded-lg border bg-card p-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">{tr('Chart of Accounts', '會計科目表', '会计科目表')}</label>
            <div role="radiogroup" aria-label={tr('Chart of Accounts', '會計科目表', '会计科目表')} className="mt-1 divide-y border rounded-lg overflow-hidden">
              {([
                { value: 'predefined' as const, testid: 'coa-choice-predefined', label: tr('Use system pre-defined COA', '使用系統預設會計科目表', '使用系统预设会计科目表') },
                { value: 'import' as const, testid: 'coa-choice-import', label: tr('Import from Excel template', '從 Excel 範本導入', '从 Excel 范本导入') },
              ]).map(opt => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors ${
                    coaChoice === opt.value ? 'bg-primary/5 font-medium' : 'hover:bg-muted/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="coa-choice"
                    data-testid={opt.testid}
                    value={opt.value}
                    checked={coaChoice === opt.value}
                    onChange={() => chooseCoa(opt.value)}
                    className="h-4 w-4 accent-primary"
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {coaChoice === 'predefined' && (
            <>
              <div>
                <label className="text-xs font-medium text-muted-foreground">{tr('Industry Classification', '行業分類', '行业分类')}</label>
                <select value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                  className="mt-1 block w-full px-3 py-2 border rounded bg-background text-sm">
                  <option value="">{tr('— Select industry —', '— 選擇行業 —', '— 选择行业 —')}</option>
                  <option value="professional">{tr('Professional & Business Services', '專業及商業服務', '专业及商业服务')}</option>
                  <option value="finance">{tr('Financial Services', '金融服務', '金融服务')}</option>
                  <option value="trading">{tr('Trading & Logistics', '貿易及物流', '贸易及物流')}</option>
                  <option value="tourism">{tr('Tourism', '旅遊', '旅游')}</option>
                  <option value="it">{tr('Innovation & Technology', '創新及科技', '创新及科技')}</option>
                  <option value="fintech">{tr('Fintech', '金融科技', '金融科技')}</option>
                  <option value="medical">{tr('Medical & Healthcare', '醫療及保健', '医疗及保健')}</option>
                  <option value="education">{tr('Education', '教育', '教育')}</option>
                  <option value="construction">{tr('Construction & Real Estate', '建築及地產', '建筑及地产')}</option>
                  <option value="ict">{tr('ICT & Telecommunications', '資訊及通訊科技', '资讯及通讯科技')}</option>
                  <option value="manufacturing">{tr('Manufacturing', '製造業', '制造业')}</option>
                </select>
              </div>

              <div data-testid="coa-preview">
                <CoaPreview
                  industry={form.industry}
                  mode={coaMode}
                  onModeChange={setCoaMode}
                  customAccounts={customAccounts}
                  onCustomAccountsChange={setCustomAccounts}
                  removedCodes={removedCodes}
                  onRemovedCodesChange={setRemovedCodes}
                  openingBalances={openingBalances}
                  onOpeningBalancesChange={setOpeningBalances}
                  onFinalAccountsChange={setFinalAccounts}
                />
              </div>
            </>
          )}

          {coaChoice === 'import' && (
            <div data-testid="coa-import-branch" className="space-y-3 border rounded-lg p-3">
              <p className="text-xs text-muted-foreground">
                {tr('Upload your chart of accounts with opening balances, or download an example file.', '上載您的會計科目表及承前結餘，或下載範例檔案。', '上传您的会计科目表及承前结余，或下载范例档案。')}
              </p>
              <div className="flex gap-2 flex-wrap">
                <button type="button" data-testid="coa-template-download" onClick={downloadImportTemplate}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border rounded text-xs font-medium hover:bg-muted/50">
                  <Download className="h-3.5 w-3.5" /> {tr('Download example', '下載範例', '下载范例')}
                </button>
                <button type="button" data-testid="coa-import-open" onClick={() => coaFileRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-medium">
                  <Upload className="h-3.5 w-3.5" /> {tr('Upload COA file', '上載會計科目檔案', '上载会计科目档案')}
                </button>
                <input ref={coaFileRef} type="file" accept=".xlsx,.xls" data-testid="coa-import-file-input" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleCoaFile(f); e.target.value = ''; }} />
              </div>
              {mapLoading && (
                <div data-testid="coa-map-loading" className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                  {tr('Reading file and mapping accounts…', '正在讀取檔案並對應科目…', '正在读取档案并对应科目…')}
                </div>
              )}
              {mapError && <div data-testid="coa-map-error" className="text-xs text-destructive">{mapError}</div>}
              {mapNotice && <div data-testid="coa-map-notice" className="text-xs text-amber-900 dark:text-amber-100">{mapNotice}</div>}
              {mapProposals && (
                <CoaMapReview proposals={mapProposals} onApply={applyMappedCoa} onEdit={() => setMapApplied(false)} provider={mapProvider} usage={mapUsage} draftKey={COA_DRAFT_REVIEW_KEY} />
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">{tr('FY Start Month', '會計年度起始月', '会计年度起始月')}</label>
              <select value={form.fy_start} onChange={e => setForm(f => ({ ...f, fy_start: e.target.value }))}
                className="mt-1 block w-full px-3 py-2 border rounded text-sm bg-background">
                {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                  <option key={m} value={m}>{new Date(2024, parseInt(m)-1).toLocaleString('default', { month: 'long' })}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">{tr('FY End Month', '會計年度結束月', '会计年度结束月')}</label>
              <select value={form.fy_end} onChange={e => setForm(f => ({ ...f, fy_end: e.target.value }))}
                className="mt-1 block w-full px-3 py-2 border rounded text-sm bg-background">
                {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                  <option key={m} value={m}>{new Date(2024, parseInt(m)-1).toLocaleString('default', { month: 'long' })}</option>
                ))}
              </select>
            </div>
          </div>

          <WizardFooter
            onBack={() => goToStep(STEP_COMPANY)}
            onCancel={() => nav(returnTo)}
            onNext={() => goToStep(STEP_REVIEW)}
            nextDisabled={!accountingReady}
            nextLabel={tr('Review company', '確認公司資料', '确认公司资料')}
          />
        </div>
      )}

      {activeStep === STEP_REVIEW && (
        <div className="rounded-lg border bg-card p-5 space-y-4">
          <SummaryRow label={tr('Company', '公司', '公司')} value={form.company_name} onEdit={() => goToStep(STEP_COMPANY)} />
          <SummaryRow label={tr('Contact', '聯絡人', '联络人')} value={form.contact_name || '—'} onEdit={() => goToStep(STEP_COMPANY)} />
          <SummaryRow
            label={tr('Client login', '客戶登入', '客户登录')}
            value={showLogin ? form.contact_email : tr('No login yet', '暫無登入', '暂无登录')}
            onEdit={() => goToStep(STEP_COMPANY)}
          />
          <SummaryRow
            label={tr('Chart of Accounts', '會計科目表', '会计科目表')}
            value={coaChoice === 'import'
              ? tr(`Imported COA — ${finalAccounts.length} accounts`, `導入科目表——${finalAccounts.length} 個科目`, `导入科目表——${finalAccounts.length} 个科目`)
              : tr(`${finalAccounts.length} accounts`, `${finalAccounts.length} 個科目`, `${finalAccounts.length} 个科目`)}
            onEdit={() => goToStep(STEP_ACCOUNTING)}
          />
          <SummaryRow
            label={tr('Financial year', '會計年度', '会计年度')}
            value={`${monthLabel(form.fy_start)} – ${monthLabel(form.fy_end)}`}
            onEdit={() => goToStep(STEP_ACCOUNTING)}
          />

          {dupConflict && (
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4 space-y-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
                <div>
                  <p className="font-semibold text-amber-900 dark:text-amber-100">
                    {tr('A client with this name already exists', '已有同名客戶', '已有同名客户')}
                  </p>
                  <p className="mt-0.5 text-sm text-amber-800 dark:text-amber-200">
                    {tr(
                      `“${dupConflict.display_name || form.company_name}” was created on ${String(dupConflict.created_at).slice(0, 10)}. Switch to it, or create another client with the same name.`,
                      `「${dupConflict.display_name || form.company_name}」建立於 ${String(dupConflict.created_at).slice(0, 10)}。可切換至該客戶，或仍然建立同名客戶。`,
                      `「${dupConflict.display_name || form.company_name}」建立于 ${String(dupConflict.created_at).slice(0, 10)}。可切换至该客户，或仍然建立同名客户。`,
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pl-8">
                <button type="button" onClick={switchToExistingClient}
                  className="rounded-lg bg-amber-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-800">
                  {tr('Switch to this client', '切換至該客戶', '切换至该客户')}
                </button>
                <button type="button" onClick={() => createMut.mutate({ allowDuplicate: true })} disabled={createMut.isPending}
                  className="rounded-lg border border-amber-400 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:text-amber-100 dark:hover:bg-amber-900/40">
                  {tr('Create anyway', '仍要建立', '仍要建立')}
                </button>
                <button type="button" onClick={() => setDupConflict(null)}
                  className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:underline">
                  {tr('Dismiss', '關閉', '关闭')}
                </button>
              </div>
            </div>
          )}

          <WizardFooter
            onBack={() => goToStep(STEP_ACCOUNTING)}
            onCancel={() => nav(returnTo)}
            onSubmit={() => createMut.mutate(undefined)}
            submitDisabled={!canSubmit}
            submitLabel={createMut.isPending ? tr('Creating…', '建立中…', '建立中…') : tr('Create Client', '建立客戶', '建立客户')}
          />
        </div>
      )}
    </div>
  );
}

function monthLabel(month: string) {
  return new Date(2024, Math.max(0, Math.min(11, parseInt(month, 10) - 1 || 0))).toLocaleString('default', { month: 'long' });
}

function CheckoutTimeline({ activeStep, onStepClick }: {
  activeStep: number;
  onStepClick: (step: number) => void;
}) {
  return (
    <nav aria-label={tr('Company setup progress', '公司設定進度', '公司设定进度')} className="bg-card border rounded-xl p-3">
      <ol className="flex items-center gap-1 overflow-x-auto">
        {STEP_META.map((step, index) => {
          const done = step.id < activeStep;
          const current = step.id === activeStep;
          const clickable = done && step.id !== STEP_COMPLETE;
          return (
            <li key={step.id} className="flex items-center min-w-fit">
              <button
                type="button"
                onClick={() => clickable && onStepClick(step.id)}
                disabled={!clickable}
                aria-current={current ? 'step' : undefined}
                className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-left transition-colors ${
                  current ? 'bg-primary/10 text-primary' : clickable ? 'hover:bg-muted' : 'text-muted-foreground'
                }`}
              >
                <span className={`flex h-6 w-6 items-center justify-center rounded-full border text-xs ${
                  done ? 'border-green-800 bg-green-800 text-white'
                    : current ? 'border-primary text-primary'
                    : 'border-border text-muted-foreground'
                }`}>
                  {done ? <CheckCircle2 className="h-4 w-4" /> : step.id}
                </span>
                <span className="whitespace-nowrap text-xs font-medium sm:text-sm">{step.label}</span>
              </button>
              {index < STEP_META.length - 1 && <ChevronRight className="mx-1 h-4 w-4 shrink-0 text-muted-foreground" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function WizardFooter({ onBack, onCancel, onNext, onSubmit, nextDisabled, submitDisabled, nextLabel, submitLabel }: {
  onBack?: () => void;
  onCancel: () => void;
  onNext?: () => void;
  onSubmit?: () => void;
  nextDisabled?: boolean;
  submitDisabled?: boolean;
  nextLabel?: string;
  submitLabel?: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
      <div className="flex gap-2">
        {onBack && (
          <button type="button" onClick={onBack} className="px-4 py-2 border rounded text-sm">
            {tr('Back', '返回', '返回')}
          </button>
        )}
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded text-sm">
          {tr('Cancel', '取消', '取消')}
        </button>
      </div>
      {onNext && (
        <button type="button" onClick={onNext} disabled={nextDisabled}
          className="px-5 py-2 bg-primary text-primary-foreground rounded text-sm font-medium disabled:opacity-50">
          {nextLabel}
        </button>
      )}
      {onSubmit && (
        <button type="button" onClick={onSubmit} disabled={submitDisabled}
          className="px-5 py-2 bg-primary text-primary-foreground rounded text-sm font-medium disabled:opacity-50">
          {submitLabel}
        </button>
      )}
    </div>
  );
}

function SummaryRow({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b pb-2 last:border-b-0 last:pb-0">
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm font-medium">{value}</div>
      </div>
      <button type="button" onClick={onEdit} className="text-xs text-primary hover:underline">
        {tr('Edit', '修改', '修改')}
      </button>
    </div>
  );
}

function CredentialRow({ label, value, copied, onCopy }: {
  label: string; value: string; copied: boolean; onCopy: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="text-xs text-muted-foreground">{label}:</div>
      <div className="flex items-center gap-2">
        <div className="font-mono">{value}</div>
        <button onClick={onCopy} className="text-xs px-2 py-1 border rounded hover:bg-muted flex items-center gap-1" title="Copy">
          {copied ? <><Check className="h-3 w-3 text-green-900 dark:text-green-200" /> {tr('Copied', '已複製', '已复制')}</> : <><Copy className="h-3 w-3" /> {tr('Copy', '複製', '复制')}</>}
        </button>
      </div>
    </div>
  );
}
